import s from './VideoPlayer.module.scss'
import { FC, useEffect, useRef, useState } from 'react'

import { ControlBar, Player, PlayerReference } from 'video-react'
import cn from 'clsx'
import {
  useIntersectionObserver,
  useIsomorphicLayoutEffect,
  useWindowSize,
} from 'usehooks-ts'
import { Play } from '@components/icons'
import { isMobileWidth } from '@utils/helpers'
import { RequestFile } from '@generated/model/models'

type VideoPlayerProps = {
  data: {
    video?: RequestFile | null
    mobile_video?: RequestFile | null
    video_url?: string | null
    mobile_video_url?: string | null
    image?: RequestFile | null
    mobile_image?: RequestFile | null
  }
  preload?: 'auto' | 'metadata' | 'none'
  controls?: boolean
  className?: string
}

const isVimeoUrl = (url?: string | null): url is string =>
  !!url && url.includes('vimeo.com')

// Build a player.vimeo.com embed URL from any vimeo.com link, e.g.
// https://vimeo.com/123456789           -> id 123456789
// https://vimeo.com/123456789/abcdef    -> id 123456789, private hash abcdef
// https://player.vimeo.com/video/123... -> id 123...
const getVimeoEmbedUrl = (url: string, controls: boolean): string => {
  const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/(\w+))?/)
  if (!match) return ''

  const [, id, hash] = match
  const params = new URLSearchParams()
  if (hash) params.set('h', hash)
  // Never show Vimeo's own UI — playback is driven by the custom overlay
  // button via the Vimeo player postMessage API.
  params.set('controls', '0')
  // Mirror the html5 player: autoplay + muted only when controls are disabled.
  params.set('autoplay', controls ? '0' : '1')
  params.set('muted', controls ? '0' : '1')
  params.set('loop', '1')
  params.set('playsinline', '1')

  return `https://player.vimeo.com/video/${id}?${params.toString()}`
}

const VideoPlayer: FC<VideoPlayerProps> = ({
  data,
  controls = false,
  className = '',
  ...props
}) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)
  const [source, setSource] = useState<string>('')
  const [poster, setPoster] = useState<string>('')

  const windowSize = useWindowSize()
  const isMobile = isMobileWidth(windowSize.width)

  // Prefer an external URL over an uploaded file for the active breakpoint.
  const mobileSource = data.mobile_video_url || data.mobile_video
  const activeSource = (
    isMobile && mobileSource ? mobileSource : data.video_url || data.video
  ) as string | undefined

  const isVimeo = isVimeoUrl(activeSource)
  const vimeoEmbedUrl = isVimeoUrl(activeSource)
    ? getVimeoEmbedUrl(activeSource, controls)
    : ''

  const playerRef = useRef<PlayerReference>(null)
  const vimeoRef = useRef<HTMLIFrameElement>(null)
  const ref = useRef<HTMLDivElement | null>(null)

  const postToVimeo = (method: string, value?: string) => {
    vimeoRef.current?.contentWindow?.postMessage(
      JSON.stringify(value !== undefined ? { method, value } : { method }),
      '*'
    )
  }

  const playerEntry = useIntersectionObserver(ref, {
    threshold: 0.01,
  })

  const isPlayerVisible = !!playerEntry?.isIntersecting

  useEffect(() => {
    const player = playerRef.current

    if (player) {
      const handlePlay = () => {
        setIsPlaying(true)
      }
      const handlePause = () => {
        setIsPlaying(false)
      }

      player.subscribeToStateChange((state) => {
        setIsLoaded(true)

        // Loop background (non-controls) videos.
        if (state.ended && !controls) {
          player.seek(0)
          player.play()
          return
        }

        if (state.paused) {
          handlePause()
        }

        if (!state.paused) {
          handlePlay()
        }
      })
    }
  }, [])

  useEffect(() => {
    if (!isLoaded) return

    if (!isPlaying && isPlayerVisible && !controls) {
      setIsPlaying(true)
      playerRef.current?.play()
    } else if (!isPlayerVisible && isPlaying) {
      setIsPlaying(false)
      playerRef.current?.pause()
    }
  }, [isPlayerVisible, isPlaying, isLoaded])

  useIsomorphicLayoutEffect(() => {
    if (props.preload == 'auto' || isPlayerVisible) {
      setSource(activeSource || '')
      playerRef.current?.load()
    }
    if (isMobile && mobileSource) {
      setPoster(data.mobile_image as unknown as string)
    } else {
      setPoster(data.image as unknown as string)
    }
  }, [isMobile, activeSource, mobileSource, isPlayerVisible])

  useIsomorphicLayoutEffect(() => {
    playerRef.current?.load()
  }, [windowSize.width])

  // Track the Vimeo player's play/pause state via its postMessage API so the
  // custom overlay button reflects and controls playback.
  useEffect(() => {
    if (!isVimeo) return

    const handleMessage = (event: MessageEvent) => {
      if (!vimeoRef.current || event.source !== vimeoRef.current.contentWindow)
        return

      let payload: { event?: string }
      try {
        payload =
          typeof event.data === 'string' ? JSON.parse(event.data) : event.data
      } catch {
        return
      }

      if (payload?.event === 'ready') {
        postToVimeo('addEventListener', 'play')
        postToVimeo('addEventListener', 'pause')
      } else if (payload?.event === 'play') {
        setIsPlaying(true)
      } else if (payload?.event === 'pause') {
        setIsPlaying(false)
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [isVimeo, vimeoEmbedUrl])

  const handleClick = () => {
    if (isVimeo) {
      postToVimeo(isPlaying ? 'pause' : 'play')
      return
    }

    const player = playerRef.current
    if (player) {
      if (isPlaying) {
        player.pause()
      } else {
        player.play()
      }
    }
  }

  return (
    <div
      className={cn(
        s.root,
        controls ? s.withControls : '',
        isPlaying ? s.isPlaying : '',
        className
      )}
      ref={ref}
    >
      <div className={s.videoInner}>
        {isVimeo ? (
          <div className={s.vimeoWrapper}>
            {(props.preload == 'auto' || isPlayerVisible) && (
              <iframe
                ref={vimeoRef}
                className={s.vimeoIframe}
                src={vimeoEmbedUrl}
                title="Vimeo video player"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            )}
            {/* Vimeo has no controls, so clicking the playing video pauses it */}
            {controls && isPlaying && (
              <button
                className={s.pauseTarget}
                onClick={handleClick}
                aria-label="Pause"
              />
            )}
          </div>
        ) : (
          <Player
            ref={playerRef}
            preload={controls ? 'none' : 'auto'}
            src={source}
            poster={poster as unknown as string}
            muted={controls ? false : true}
            autoPlay={controls ? false : true}
            playsInline
            fluid
            {...props}
          >
            {controls ? (
              <ControlBar autoHide={true} autoHideTime={2000} />
            ) : (
              <ControlBar disableCompletely={true} />
            )}
          </Player>
        )}
        {controls && !isPlaying && (
          <button className={s.playerButton} onClick={handleClick}>
            <span className={s.triangle}>
              <Play className={s.triangleIcon} />
            </span>
          </button>
        )}
      </div>
    </div>
  )
}

export default VideoPlayer
